/**
 * Card representation and utilities.
 *
 * A card is an integer in [0, 52). `rank = Math.floor(card / 4)` in [0, 13)
 * where 0 = deuce (2) ... 12 = ace (A). `suit = card % 4`.
 *
 * This module has no environment-dependent dependencies (no `fs`, no DOM,
 * no `Math.random`), so it can run identically in the browser, Node, and
 * web workers.
 */

export const NUM_RANKS = 13;
export const NUM_SUITS = 4;
export const DECK_SIZE = 52;

/** Rank characters, indexed 0 (deuce) .. 12 (ace). */
export const RANK_CHARS = "23456789TJQKA" as const;

/** Suit characters, indexed 0..3. */
export const SUIT_CHARS = "cdhs" as const;

export type Card = number;

export function rankOf(card: Card): number {
  return Math.floor(card / 4);
}

export function suitOf(card: Card): number {
  return card % 4;
}

export function makeCard(rank: number, suit: number): Card {
  return rank * 4 + suit;
}

export function isValidCard(card: number): card is Card {
  return Number.isInteger(card) && card >= 0 && card < DECK_SIZE;
}

/**
 * Format a card as a two-character string, e.g. `card 51 -> "As"`.
 */
export function cardToString(card: Card): string {
  if (!isValidCard(card)) {
    throw new RangeError(`Invalid card: ${card}`);
  }
  const rankChar = RANK_CHARS[rankOf(card)];
  const suitChar = SUIT_CHARS[suitOf(card)];
  return `${rankChar}${suitChar}`;
}

/**
 * Parse a two-character card string (e.g. "As", "Td", "2c") into a card
 * integer. Case-insensitive on both rank and suit characters.
 */
export function parseCard(str: string): Card {
  if (str.length !== 2) {
    throw new SyntaxError(`Invalid card string: "${str}"`);
  }
  const rankChar = str[0]!.toUpperCase();
  const suitChar = str[1]!.toLowerCase();
  const rank = RANK_CHARS.indexOf(rankChar);
  const suit = SUIT_CHARS.indexOf(suitChar);
  if (rank === -1 || suit === -1) {
    throw new SyntaxError(`Invalid card string: "${str}"`);
  }
  return makeCard(rank, suit);
}

/** Parse a space-separated string of cards, e.g. "As Kd 2c". */
export function parseCards(str: string): Card[] {
  return str
    .trim()
    .split(/\s+/)
    .filter((s) => s.length > 0)
    .map(parseCard);
}

export function cardsToString(cards: readonly Card[]): string {
  return cards.map(cardToString).join(" ");
}

/** Returns a fresh standard 52-card deck, in canonical order (0..51). */
export function fullDeck(): Card[] {
  const deck = new Array<Card>(DECK_SIZE);
  for (let i = 0; i < DECK_SIZE; i++) deck[i] = i;
  return deck;
}

// ---------------------------------------------------------------------------
// Seedable PRNG
// ---------------------------------------------------------------------------

/** A seedable pseudo-random number generator. `next()` returns a float in [0, 1). */
export interface Rng {
  next(): number;
  /** Returns an integer in [0, maxExclusive). */
  nextInt(maxExclusive: number): number;
}

/**
 * mulberry32: small, fast, seedable 32-bit PRNG. Not cryptographically
 * secure — used for deterministic simulations/tests only. `Math.random`
 * must never be used in this package, so all randomness flows through
 * this PRNG.
 */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;

  function next(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  function nextInt(maxExclusive: number): number {
    return Math.floor(next() * maxExclusive);
  }

  return { next, nextInt };
}

/**
 * Fisher-Yates shuffle. Mutates and returns `cards` in place. Uses the
 * supplied seedable Rng only (never Math.random).
 */
export function shuffle<T>(cards: T[], rng: Rng): T[] {
  for (let i = cards.length - 1; i > 0; i--) {
    const j = rng.nextInt(i + 1);
    const tmp = cards[i]!;
    cards[i] = cards[j]!;
    cards[j] = tmp;
  }
  return cards;
}

/** Returns a shuffled fresh 52-card deck. */
export function shuffledDeck(rng: Rng): Card[] {
  return shuffle(fullDeck(), rng);
}
